import { normaliseResponse } from '@theme/normalise-response';
import { API_ENDPOINTS } from '@theme/api-endpoints';
import { getApiRequestContext } from '@theme/api-request-context';
import { getApiLocale } from '@theme/locale-map';

/**
 * APIM Store Detail client module.
 *
 * Wraps the external Market America `store-details` API endpoint behind a
 * single async function that returns a normalised response envelope.
 *
 * Mirrors the `Store Details (Volumes)` Postman call:
 *   POST {API_ENDPOINTS.storeDetail}         (/shopify-app-proxy/store-search/v1/volumes)
 *   Headers: Content-Type: application/json, API_KEY: <api key>
 *   Body:    { siteType, languageCode, countryCode, distributorInfo, fields, volumeIds }
 *
 * Returns the same `{ ok, data, error }` envelope as `queryStoreList` so
 * caller components (store page, featured store, store quick-view) can share
 * the same success / error handling patterns.
 *
 * Usage:
 *   import { queryStoreDetail } from '@theme/query-store-detail';
 *
 *   const result = await queryStoreDetail({ volumeIds: ['269261'] });
 *   if (result.ok) {
 *     renderStore(result.data.store);
 *   } else {
 *     showError(result.error.message);
 *   }
 */

/**
 * @typedef {Object} QueryStoreDetailParams
 * @property {Array<string>|string} volumeIds       - APIM volume ID(s) to fetch (required). A bare string is wrapped into a single-element array.
 * @property {Array<string>} [fields]          - Fields to return (default: basic store field set).
 * @property {string}        [siteType]        - Site type (default: 'SHP').
 * @property {string}        [languageCode]    - Defaults to the active Shopify language (ENG fallback).
 * @property {string}        [countryCode]     - Defaults to the active Shopify country (USA fallback).
 * @property {Object}        [distributorInfo] - Distributor info object (default: non-distributor, no PPC IBV incentive).
 * @property {Object}        [extra]           - Additional top-level body keys.
 * @property {AbortSignal}   [signal]          - AbortSignal for request cancellation.
 */

/**
 * @typedef {Object} QueryStoreDetailData
 * @property {Object|null}   store      - The first store from the APIM `hits` array (convenience for single-ID callers).
 * @property {Array<Object>} stores     - All stores in the APIM `hits` array.
 * @property {number}        totalCount - The `totalHits` value from the APIM response.
 * @property {Object}        raw        - The unmodified parsed JSON response, for consumers that need fields not surfaced above.
 *
 * @typedef {Object} QueryStoreDetailSuccess
 * @property {true}                    ok
 * @property {QueryStoreDetailData}    data
 * @property {null}                    error
 */

/**
 * @typedef {Object} QueryStoreDetailError
 * @property {false}  ok
 * @property {null}   data
 * @property {{ message: string, status: number | null }} error
 */

/** @typedef {QueryStoreDetailSuccess | QueryStoreDetailError} QueryStoreDetailResult */

/** Default request values mirroring the `Store Details (Volumes)` Postman call. */
const DEFAULTS = {
  siteType: 'SHP',
  fields: [
    'catalogId',
    'volumeId',
    'catalogName',
    'catalogLogo',
    'derived',
    'wwwLink',
    'launchDateV2',
    'commissionType',
    'productType',
    'ibvOnly',
    'ibvStandard',
    'convIbvStandard',
    'convIbvOnly',
    'stdCashback',
    'incCashback',
    'shopPoints',
  ],
};

/**
 * The APIM store-detail (volumes) endpoint (same-origin App Proxy path).
 * Centralized in `@theme/api-endpoints` — formerly the
 * `custom.apim_store_detail_url` shop metafield rendered into `Theme.routes`.
 *
 * @returns {string}
 */
const getEndpoint = () => API_ENDPOINTS.storeDetail;

/**
 * Reads the APIM API key configured via the `custom.apim_api_key` shop
 * metafield and rendered into `Theme.routes`. The same key is used for
 * product-search, store-search, product-detail, and store-detail endpoints.
 *
 * @returns {string}
 */
const getApiKey = () => {
  const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
  return routes.apim_api_key || '';
};

/**
 * Builds the request headers. The API authenticates via the `API_KEY` header
 * (Azure APIM subscription credential) — NOT an Authorization bearer token.
 *
 * @param {string} apiKey
 * @returns {HeadersInit}
 */
const buildHeaders = (apiKey) => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  API_KEY: apiKey,
});

/**
 * Normalises the `volumeIds` param into an array of strings. Accepts either a
 * single ID string or an array of IDs.
 *
 * @param {Array<string>|string|undefined} volumeIds
 * @returns {Array<string>}
 */
const normaliseVolumeIds = (volumeIds) => {
  if (Array.isArray(volumeIds)) {
    return volumeIds.map(String);
  }
  if (typeof volumeIds === 'string' && volumeIds !== '') {
    return [volumeIds];
  }
  return [];
};

/**
 * Builds the request body matching the `Store Details (Volumes)` Postman schema.
 *
 * @param {QueryStoreDetailParams} params
 * @returns {Object}
 */
const buildBody = (params) => {
  const { languageCode: localeLanguage, countryCode: localeCountry } = getApiLocale();
  const {
    volumeIds,
    fields = DEFAULTS.fields,
    siteType = DEFAULTS.siteType,
    languageCode = localeLanguage,
    countryCode = localeCountry,
    distributorInfo = {},
    extra = {},
  } = params;

  const context = getApiRequestContext(countryCode, siteType);

  return {
    siteType: context.siteType,
    languageCode,
    countryCode,
    distributorInfo: { ...context.distributorInfo, ...distributorInfo },
    fields,
    volumeIds: normaliseVolumeIds(volumeIds),
    ...extra,
  };
};

/**
 * Queries the external APIM `store-details` endpoint.
 *
 * @param {QueryStoreDetailParams} params
 * @returns {Promise<QueryStoreDetailResult>}
 * @throws {DOMException} Re-throws AbortError so callers can distinguish
 *   cancellation from real failures.
 */
export async function queryStoreDetail(params) {
  const { signal } = params;
  const endpoint = getEndpoint();
  const apiKey = getApiKey();

  const volumeIds = normaliseVolumeIds(params.volumeIds);

  if (volumeIds.length === 0) {
    return {
      ok: false,
      data: null,
      error: { message: 'At least one volume ID is required.', status: null },
    };
  }

  if (!endpoint) {
    return {
      ok: false,
      data: null,
      error: {
        message: 'APIM store detail endpoint is not configured (@theme/api-endpoints).',
        status: null,
      },
    };
  }

  if (!apiKey) {
    return {
      ok: false,
      data: null,
      error: {
        message: 'APIM API key is not configured (shop metafield custom.apim_api_key).',
        status: null,
      },
    };
  }

  const headers = buildHeaders(apiKey);
  const body = JSON.stringify(buildBody(params));

  // Note: this is a same-origin App Proxy call (the store proxies
  // /shopify-app-proxy/* to the APIM gateway on its own domain), so there is no
  // CORS preflight. A "Failed to fetch" here points at the proxy/gateway, not a
  // cross-origin config the theme can influence.
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body,
      signal,
    });

    if (!response.ok) {
      let errorMessage = `Store detail fetch failed (${response.status})`;

      try {
        const errorJson = await response.json();
        if (errorJson.message) {
          errorMessage = errorJson.message;
        } else if (errorJson.error) {
          errorMessage =
            typeof errorJson.error === 'string'
              ? errorJson.error
              : errorJson.error.message || errorMessage;
        }
      } catch {
        // Response body wasn't JSON — keep the generic message
      }

      return {
        ok: false,
        data: null,
        error: { message: errorMessage, status: response.status },
      };
    }

    const json = await response.json();

    return normaliseResponse(json, 'store-detail');
  } catch (error) {
    // Re-throw AbortError so callers can distinguish cancellation
    if (error.name === 'AbortError') {
      throw error;
    }

    return {
      ok: false,
      data: null,
      error: {
        message: error.message || 'An unexpected error occurred.',
        status: null,
      },
    };
  }
}
